<template>
    <div ref="drawer" class="e-drawer" :class="drawerClasses" :tabindex="sidebar ? -1 : tabindex" @scroll="handleScroll"
         :style="drawerStyles">

        <!-- Real snap points html elements because css scroll-snap works with elements -->
        <template v-if="!sidebar">
            <div v-for="(snap, index) in computedSnapPoints" :key="'snap-' + index" class="snap"
                 :class="{ initial: snap.initial }" :style="{ '--snap': snap.value }"/>
            <div v-if="!isFixedSnap" class="snap snap-bottom" data-snap="2"/>
        </template>

        <!-- Floating above the sheet -->
        <div v-if="$slots.above && !sidebar" class="sheet-above" :class="{ 'is-hidden': isAboveHidden }">
            <slot name="above"/>
        </div>

        <!-- Handle and buttons ABOVE the cover image (z-index: 11) -->
        <div class="handle"/>

        <div class="top-actions-container">
            <b-btn v-if="!noCloseButton && sidebar && showPreviousButton" variant="light" pill
                   class="ratio-1x1 btn-previous" @click.stop="$emit('close')">
                <i :class="[fontAwesomePrefix, 'fa-chevron-left']"/>
            </b-btn>
            <b-btn v-if="!noShareButton && (!showFixedHeader || sidebar) && (!collapseTopActions || !coverImageVisible)" variant="light" pill
                   class="ratio-1x1 btn-share" @click.stop="$emit('share')">
                <i :class="[fontAwesomePrefix, 'fa-arrow-up-from-square']"/>
            </b-btn>
            <b-btn v-if="!noCloseButton && (!showPreviousButton || !sidebar) && (!collapseTopActions || !coverImageVisible)" variant="light" pill
                   class="ratio-1x1 btn-close" @click.stop="$emit('close')">
                <i :class="[fontAwesomePrefix, 'fa-xmark']"/>
            </b-btn>
            <div v-if="$slots['top-actions-end']" class="top-actions-end">
                <slot name="top-actions-end"/>
            </div>
        </div>

        <div class="sheet-container" :class="{ 'has-cover': $slots['cover-image'] }">
            <!-- Cover image -->
            <div v-if="$slots['cover-image']" class="sheet-cover-wrapper">
                <div class="sheet-cover-image">
                    <slot name="cover-image"/>
                </div>
            </div>

            <div ref="sheetWrapper" class="sheet-wrapper" :class="{ 'has-cover': $slots['cover-image'] }">
                <aside ref="sheet" class="sheet" data-snap="0">

                    <div v-if="title" class="fixed-header" :class="{ 'is-visible': showFixedHeader }">
                        <div class="drawer-title">{{ title }}</div>
                    </div>

                    <header class="sheet-header" :class="[{ 'sticky-header': stickyHeader }, headerClass]" @click="expandOnHeaderClick ? snapToMax() : $emit('header-click')">
                        <div class="sheet-header-content">
                            <div v-if="title" ref="drawerTitle" class="drawer-title" :class="{'drawer-title-full': coverImageVisible, 'no-buttons': noShareButton && noCloseButton}" @click="snapToMax()">
                                <div v-if="label" class="drawer-label text-muted">{{ $tr(label) }}</div>
                                <div class="drawer-text">{{ $tr(title) }}</div>
                            </div>
                            <slot name="header"/>
                        </div>
                    </header>

                    <section ref="sheetContent" class="sheet-content" :class="{ 'p-0': noContentPadding }">
                        <div v-if="$slots['header-actions']" class="sheet-header-actions">
                            <slot name="header-actions"/>
                        </div>
                        <slot/>
                    </section>

                    <footer v-if="$slots.footer" class="sheet-footer">
                        <slot name="footer"/>
                    </footer>
                </aside>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: `e-drawer`,
    props: {
        /** Array of snap point values (e.g., ['25%', '50%', '75%']) */
        snapPoints: {
            type: Array,
            default: () => [`100%`]
        },
        /** Index of the initial snap point (0-based) */
        initialSnap: {
            type: Number,
            default: 0
        },
        /** Enable swipe to dismiss functionality */
        swipeToDismiss: {
            type: Boolean,
            default: false
        },
        /** Enable nested scroll mode for scrollable content */
        nestedScroll: {
            type: Boolean,
            default: false
        },
        /** Expand sheet before allowing content scroll */
        expandToScroll: {
            type: Boolean,
            default: false
        },
        /** Tab index for keyboard accessibility */
        tabindex: {
            type: [Number, String],
            default: 0
        },
        sidebar: {
            type: Boolean,
            default: false
        },
        showNav: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: ``
        },
        label: {
            type: String,
            default: ``
        },
        noCloseButton: {
            type: Boolean,
            default: false
        },
        noShareButton: {
            type: Boolean,
            default: false
        },
        collapseTopActions: {
            type: Boolean,
            default: false
        },
        stickyHeader: {
            type: Boolean,
            default: false
        },
        topMobileMargin: {
            type: String,
            default: `80px`
        },
        topSidebarMargin: {
            type: String,
            default: `0px`
        },
        showPreviousButton: {
            type: Boolean,
            default: false
        },
        noContentPadding: {
            type: Boolean,
            default: false
        },
        /** Clicking the sheet header snaps the drawer to its fully open position */
        expandOnHeaderClick: {
            type: Boolean,
            default: false
        },
        /** A single fixed snap point value (e.g., '50%'). When set, the drawer locks at this position and cannot be scrolled. Use instead of snapPoints. */
        fixedSnapPoint: {
            type: String,
            default: ``
        },
        /** Height of the cover image in pixels */
        coverHeight: {
            type: [Number, String],
            default: 152
        },
        headerClass: {
            type: String,
            default: ``
        }
    },
    data() {
        return {
            snapPosition: `2`,
            isScrolling: false,
            sheetPosition: 0,
            isAboveHidden: false,
            trayPosition: null,
            showFixedHeader: false,
            headerObserver: null,
            coverImageVisible: false
        };
    },
    computed: {
        hasCover() {
            return Boolean(this.$slots[`cover-image`]);
        },
        shouldForceSnapStop() {
            return !this.sidebar && !this.isFixedSnap && this.hasCover && this.snapPoints.length === 1;
        },
        isFixedSnap() {
            return Boolean(this.fixedSnapPoint);
        },
        computedSnapPoints() {
            if (this.isFixedSnap) {
                return [{
                    value: this.fixedSnapPoint,
                    initial: true
                }];
            }

            const points = this.snapPoints.map((value, index) => ({
                value,
                initial: index === this.initialSnap
            }));

            if (this.shouldForceSnapStop) {
                points.push({
                    value: `var(--tray-client-height)`,
                    initial: false
                });
            }

            return points;
        },
        drawerClasses() {
            return {
                'cover-image-visible': this.coverImageVisible,
                'nested-scroll': this.nestedScroll,
                'show-nav': this.showNav,
                'expand-to-scroll': this.expandToScroll,
                'swipe-to-dismiss': this.swipeToDismiss,
                'is-scrolling': this.isScrolling,
                'fixed-snap': this.isFixedSnap,
                [`snap-position-${this.snapPosition}`]: true,
                'sidebar-drawer': this.sidebar
            };
        },
        drawerStyles() {
            const styles = {};

            if (this.sidebar) {
                styles[`--sidebar-top`] = this.topSidebarMargin;
            } else {
                styles[`--sheet-top-margin`] = this.topMobileMargin;
            }

            if (this.fixedSnapPoint) {
                styles[`--fixed-snap-height`] = this.fixedSnapPoint;
            }

            if (this.$slots[`cover-image`]) {
                const height = typeof this.coverHeight === `number` ? `${this.coverHeight}px` : this.coverHeight;
                styles[`--cover-height`] = height;
            }
            return styles;
        }
    },
    mounted() {
        this._rafId = null;

        // Set up header observer for fixed header visibility
        this.$nextTick(() => {
            this.setupHeaderObserver();
        });

        // In sidebar or fixed snap mode, skip drawer snap behavior
        if (this.sidebar || this.isFixedSnap) {
            return;
        }

        // No IntersectionObserver - use scroll-based position detection only
        this.handleViewportResize();

        if (window.visualViewport) {
            window.visualViewport.addEventListener(`resize`, this.handleViewportResize);
        }

        // Initial scroll to snap point
        this.$nextTick(() => {
            this.scrollToInitialSnap();
        });
    },
    beforeDestroy() {
        if (window.visualViewport) {
            window.visualViewport.removeEventListener(`resize`, this.handleViewportResize);
        }
        if (this.headerObserver) {
            this.headerObserver.disconnect();
        }
        if (this._rafId) {
            cancelAnimationFrame(this._rafId);
        }
    },
    methods: {
        handleViewportResize() {
            if (this.$refs.drawer) {
                const drawerHeight = this.$refs.drawer.clientHeight;
                const keyboardHeight = window.visualViewport ? window.visualViewport.offsetTop : 0;
                this.$refs.drawer.style.setProperty(`--sw-keyboard-height`, `${keyboardHeight}px`);
                this.$refs.drawer.style.setProperty(`--tray-client-height`, `${drawerHeight}px`);
                this.$refs.drawer.style.setProperty(`--sheet-min-height`, `${this.computedSnapPoints[0].value}`);
            }
        },
        updateSnapPosition(position) {
            this.snapPosition = position;
            this.$emit(`snap-position-change`, {snapPosition: position});

            if (this.swipeToDismiss && position === `2`) {
                this.$emit(`dismiss`);
            }
        },
        updateAboveHidden(value) {
            if (this.isAboveHidden === value) {
                return;
            }

            this.isAboveHidden = value;
            this.$emit(`above-hidden-change`, value);
        },
        getCoverHeight() {
            const coverHeight = typeof this.coverHeight === `number` ? this.coverHeight : parseInt(this.coverHeight, 10);
            return Number.isFinite(coverHeight) ? coverHeight : 0;
        },
        setupHeaderObserver() {
            if (!this.$refs.drawerTitle) {
                return;
            }

            this.headerObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        // Show fixed header when drawer-title is not intersecting (scrolled out of view)
                        this.showFixedHeader = !entry.isIntersecting;
                    });
                },
                {
                    root: this.$refs.drawer,
                    threshold: 0,
                    rootMargin: `-40px 0px 0px 0px`
                }
            );

            this.headerObserver.observe(this.$refs.drawerTitle);
        },
        handleScroll() {
            if (this.isFixedSnap) {
                return;
            }
            if (!this.$refs.drawer || this._rafId) {
                return;
            }
            this._rafId = requestAnimationFrame(() => {
                this._rafId = null;
                this.drawScrollState();
            });
        },
        drawScrollState() {
            const drawer = this.$refs.drawer;
            if (!drawer) {
                return;
            }

            const scrollTop = drawer.scrollTop;
            const drawerHeight = drawer.clientHeight;
            const coverHeight = this.getCoverHeight();
            const trayTopMargin = 70;

            this.sheetPosition = scrollTop;

            drawer.style.setProperty(`--sheet-position`, `${scrollTop}px`);
            drawer.style.setProperty(`--tray-client-height`, `${drawerHeight}px`);

            if (this.sidebar || this.isFixedSnap || !coverHeight) {
                return;
            }

            this.updateAboveHidden(scrollTop > drawerHeight * 0.5);
            const position = drawerHeight - scrollTop;
            this.trayPosition = position;
            drawer.style.setProperty(`--tray-position`, `${Math.max(0, position)}`);

            let coverOpacity = 0;
            if (position < -coverHeight + 10) {
                drawer.style.setProperty(`--cover-opacity`, `${coverOpacity}`);
            } else {
                coverOpacity = Math.max(0, Math.min(1, 1 - Math.abs(position / coverHeight)));
                drawer.style.setProperty(`--cover-opacity`, `${coverOpacity}`);
            }

            if (position > coverHeight - 12) {
                this.coverImageVisible = false;
            } else {
                this.coverImageVisible = true;
            }

            this.updateSnapPositionFromScroll(position, coverHeight, drawerHeight);
        },
        updateSnapPositionFromScroll(position, coverHeight, drawerHeight) {
            const rangePadding = 12;
            const shadowSpread = 24;
            const trayStop = 320;
            const range3Max = Math.min(coverHeight + rangePadding, (drawerHeight - trayStop + rangePadding) / 2);
            const range4Max = drawerHeight - trayStop + rangePadding;

            let newPosition;
            if (position < -coverHeight + rangePadding) {
                newPosition = `0`;
            } else if (position < -shadowSpread) {
                newPosition = `1`;
            } else if (position < shadowSpread) {
                newPosition = `2`;
            } else if (position < range3Max) {
                newPosition = `3`;
            } else if (position < range4Max) {
                newPosition = `4`;
            } else {
                newPosition = `5`;
            }

            if (this.snapPosition !== newPosition) {
                this.updateSnapPosition(newPosition);
            }
        },
        scrollToInitialSnap() {
            if (this.isFixedSnap) {
                return;
            }
            // Trigger a re-snap by scrolling to the content
            if (this.$refs.drawer) {
                const initialSnapEl = this.$refs.drawer.querySelector(`.snap.initial`);
                if (initialSnapEl) {
                    initialSnapEl.scrollIntoView({behavior: `instant`, block: `start`});
                }
                // Update the above element position after snap
                this.$nextTick(() => {
                    this.handleScroll();
                });
            }
        },
        /** Programmatically snap to a specific position */
        snapTo(index) {
            if (this.isFixedSnap) {
                return;
            }
            const drawer = this.$refs.drawer;
            if (!drawer) {
                return;
            }

            const snapEls = drawer.querySelectorAll(`.snap:not(.snap-bottom)`);
            if (!snapEls.length) {
                return;
            }

            const numericIndex = Number(index);
            if (!Number.isFinite(numericIndex)) {
                return;
            }

            const clampedIndex = Math.max(0, Math.min(Math.trunc(numericIndex), snapEls.length - 1));
            const wantsBeyondLast = numericIndex > snapEls.length - 1;
            const targetSnap = snapEls[clampedIndex];

            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    const maxScrollTop = Math.max(0, drawer.scrollHeight - drawer.clientHeight);
                    let nextScrollTop;

                    if (wantsBeyondLast) {
                        // No snap exists beyond the last — target tray-client-height (fully open)
                        nextScrollTop = Math.min(drawer.clientHeight, maxScrollTop);
                    } else {
                        const drawerRect = drawer.getBoundingClientRect();
                        const targetRect = targetSnap.getBoundingClientRect();
                        const targetTop = drawer.scrollTop + (targetRect.top - drawerRect.top);
                        nextScrollTop = Math.max(0, Math.min(targetTop, maxScrollTop));
                    }

                    drawer.scrollTo({
                        top: nextScrollTop,
                        behavior: `smooth`
                    });
                    if (!wantsBeyondLast) {
                        this.updateSnapPosition(clampedIndex.toString());
                    }
                });
            });
        },
        snapToMax() {
            this.snapTo(this.computedSnapPoints.length);
        },
        snapToMin() {
            if (this.isFixedSnap) return;
            const drawer = this.$refs.drawer;
            if (!drawer) return;
            const initialSnapEl = drawer.querySelector(`.snap.initial`);
            if (!initialSnapEl) return;
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    const drawerRect = drawer.getBoundingClientRect();
                    const targetRect = initialSnapEl.getBoundingClientRect();
                    const maxScrollTop = Math.max(0, drawer.scrollHeight - drawer.clientHeight);
                    const targetTop = drawer.scrollTop + (targetRect.top - drawerRect.top);
                    drawer.scrollTo({
                        top: Math.max(0, Math.min(targetTop, maxScrollTop)),
                        behavior: `smooth`
                    });
                });
            });
        },
        /** Close/dismiss the drawer */
        close() {
            this.updateSnapPosition(`2`);
            this.$emit(`dismiss`);
        }
    }
};
</script>

<style lang="scss" scoped>
// ----- Variables -----
.e-drawer {
    --sheet-background: #fff;
    --sheet-secondary-background: #fbfbfb;
    --sheet-border-radius: 12px;
    --sheet-border-color: rgba(0, 0, 0, 0.1);
    --sheet-nav-height: 0px;
    --sheet-top-margin: 80px;
    --sheet-max-height: calc(100dvh - var(--sheet-top-margin) - var(--sheet-nav-height));
    --sheet-safe-max-height: calc(var(--sheet-max-height) - env(keyboard-inset-height, var(--sw-keyboard-height, 0px)));
    --sidebar-width: var(--map-assistant-width);
    --sidebar-top: var(--page-padding-top, 0);
    --sidebar-left: var(--page-padding-left, 0);
    --cover-height: 152px;
}

// ----- Base -----
.e-drawer {
    display: block;
    position: fixed;
    pointer-events: none;
    z-index: 1002;

    &:focus-visible {
        outline: none;
    }

    &.cover-image-visible {
        pointer-events: auto;
    }
}

// ----- Handle -----
.handle {
    position: sticky;
    top: 0;
    z-index: 11;
    height: 0;
    pointer-events: none;

    &::after {
        content: "";
        display: block;
        width: 36px;
        height: 5px;
        margin: 8px auto;
        background-color: rgba(0, 0, 0, 0.2);
        border-radius: 3px;
    }
}

// ----- Top Actions -----
.top-actions-container {
    position: sticky;
    top: 0;
    z-index: 11;
    height: 0;
    pointer-events: none;

    .btn-close,
    .btn-share,
    .btn-previous {
        pointer-events: auto;
    }

    .top-actions-end {
        position: absolute;
        top: 0;
        right: 0;
        margin: 16px;
        width: 30px;
        height: 30px;
        pointer-events: auto;
    }
}

// ----- Sheet Container -----
.sheet-container {
    position: relative;
    width: 100%;
    background: var(--sheet-background);
    pointer-events: all;

    display: flex;
    flex-direction: column;
    min-height: 100%;
    flex: 1 1 auto;
}

// ----- Cover -----
.sheet-cover-wrapper {
    background-color: var(--sheet-background);
    overflow: hidden;
}

.sheet-cover-image {
    * {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        margin: 0;
        width: 100%;
        height: var(--cover-height);
    }

    img {
        display: block;
        height: var(--cover-height);
        object-fit: cover;
    }
}

// ----- Sheet Wrapper -----
.sheet-wrapper {
    position: relative;
    z-index: 1;

    display: flex;
    flex-direction: column;
    min-height: 100%;
    flex: 1 1 auto;
}

// ----- Sheet -----
.sheet {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    flex: 1 1 auto;
    overflow: clip;
    border-radius: inherit;
}

// ----- Sheet Header -----
.sheet-header {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;

    background-color: var(--sheet-background);


    .sheet-header-content {
        display: flex;
        flex-direction: column;
        padding: 20px 16px 0;
    }

    &.sticky-header {
        position: sticky;
        top: 0;
        z-index: 10;
    }
}

// ----- Fixed Header -----
.fixed-header {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 11;
    width: 100%;
    height: 0;
    padding: 0;
    margin: 0;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    will-change: opacity;

    &.is-visible {
        opacity: 1;
        visibility: visible;
        pointer-events: all;
    }

    .drawer-title {
        width: 100%;
        padding: 16px 60px 16px 20px;
        margin: 0;
        font-size: 15pt;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        border-bottom: 1px solid var(--sheet-border-color);
        background-color: var(--sheet-secondary-background);
    }
}

// ----- Drawer Title -----
.drawer-title {
    font-size: 15pt;
    font-weight: 600;
    line-height: 1.5rem;
    text-align: start;
    padding-right: 85px;

    .drawer-label {
        font-size: 11pt;
        font-weight: 400;
        // line-height: 1rem;
    }
}

// ----- Sheet Content -----
.sheet-content {
    position: relative;
    z-index: 1;
    flex: 1 1 auto;
    padding: 16px 12px;
    background-color: var(--sheet-background);
}

// ----- Sheet Footer -----
.sheet-footer {
    position: sticky;
    bottom: 0;
    z-index: 1;
    width: 100%;
    background-color: var(--sheet-background);
}

// ----- Sheet Above -----
.sheet-above {
    position: absolute;
    bottom: 0;
    z-index: 13;
    display: flex;
    width: 100%;
    padding: 8px;
    pointer-events: none;

    .btn,
    .preview-mode {
        opacity: 1;
        transform: none;
        transition: opacity 200ms ease-in-out, transform 200ms ease-in-out;
        pointer-events: auto;
        touch-action: manipulation;

        &:active {
            transform: scale(0.95);
        }
    }

    &.is-hidden .btn,
    &.is-hidden .preview-mode {
        opacity: 0;
        transform: scale(0.90);
        pointer-events: none;
    }
}

// ----- Header Actions -----
.sheet-header-actions {
    display: flex;
    gap: 8px;
    width: 100%;

    .btn {
        border-radius: 8px;
    }
}

:deep(.dropdown) {
    .btn {
        border-radius: 8px;
    }

    ul > li > a {
        min-width: 0;
    }
}

// ----- Buttons -----
.btn-close,
.btn-share,
.btn-previous {
    position: absolute;
    top: 0;
    right: 0;
    width: 30px;
    height: 30px;
    margin: 16px;
    padding: 0;

    // border: 6px solid white !important;
    // box-sizing: content-box;

    // &:focus, &:active, &:hover {
    //     border: 6px solid white !important;
    // }
}

.btn-share, .top-actions-end {
    left: calc(100% - 49px - 3.53em);
    transition: 0.2s opacity ease-out, 0.2s transform ease-out;
}

// ----- Sidebar -----
.e-drawer.sidebar-drawer {
    top: var(--sidebar-top);
    right: auto;
    bottom: 0;
    left: var(--sidebar-left);
    width: var(--sidebar-width);
    height: auto;
    max-height: none;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: auto;
    border-right: 1px solid var(--sheet-border-color);
    border-radius: 0;
    scroll-snap-type: none;
    contain: layout style;
    pointer-events: auto;
    animation: none;

    .sheet-cover-wrapper {
        position: sticky;
        top: 0;
        z-index: 0;
        margin-bottom: calc(-1 * var(--cover-height));
        height: var(--cover-height);
        cursor: pointer;
    }

    .sheet-wrapper.has-cover {
        margin-top: var(--cover-height);
    }

    .sheet-wrapper {
        height: auto;
        max-height: 100%;
        border-radius: 0;
    }

    .sheet {
        position: relative;
        max-height: 100%;
        border-radius: 0;
        cursor: default;
    }

    &:has(.btn-previous) {
        // .sheet-header {
        //     margin-top: 42px;
        // }

        .btn-previous {
            left: 0;
        }

        .btn-share {
            right: 0;
            left: auto;
        }

        .fixed-header .drawer-title {
            padding-left: 62px;
        }

        .sticky-header .drawer-title {
            padding-left: 62px;
        }
    }

    .sheet-content {
        display: flex;
        flex: 1;
        flex-direction: column;
        overflow-y: auto;
    }

    .handle {
        display: none;
    }
}

// ----- Mobile -----
.e-drawer:not(.sidebar-drawer) {
    right: 0;
    bottom: calc(env(safe-area-inset-bottom, 0));
    left: 0;
    height: var(--sheet-max-height);
    max-height: var(--sheet-safe-max-height);
    overflow-y: scroll;
    overscroll-behavior-y: none;
    border-radius: var(--sheet-border-radius) var(--sheet-border-radius) 0 0;
    will-change: scroll-position;
    scroll-snap-type: y mandatory;
    scroll-snap-stop: always;
    contain: strict;
    animation: initial-snap 0.01s both;

    &.show-nav {
        --sheet-nav-height: 60px;
        bottom: calc(env(safe-area-inset-bottom, 0) + 60px);
        z-index: 1000;
    }

    .fixed-header.is-visible .drawer-title {
        top: 0px;
        border-radius: var(--sheet-border-radius) var(--sheet-border-radius) 0 0;
        border-top: 1px solid var(--sheet-border-color);
    }

    .drawer-title:not(.drawer-title-full) .drawer-text {
        overflow: hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .drawer-title.drawer-title-full, .drawer-title.no-buttons {
        padding-right: 0;
    }

    .drawer-label {
        // line-height: 0.85rem;
        margin-top: -7px;
    }

    &::-webkit-scrollbar {
        display: none;
    }

    &:focus-visible .handle {
        outline: auto;
        outline-offset: 4px;
    }

    &.cover-image-visible {
        .sheet-wrapper, .sheet {
            border-radius: 0;
        }
    }

    .sheet-above {
        align-items: flex-end;
    }

    .sheet-container {
        border-radius: var(--sheet-border-radius) var(--sheet-border-radius) 0 0;
    }

    .sheet-wrapper {
        border-radius: var(--sheet-border-radius) var(--sheet-border-radius) 0 0;
        transition: border-radius 0.2s ease-out;
    }

    .sheet-wrapper:not(:has(.sticky-header)) {
        border-top: 1px solid var(--sheet-border-color);
        border-radius: var(--sheet-border-radius) var(--sheet-border-radius) 0 0;
    }

    .sheet {
        min-height: var(--sheet-min-height);
        height: 100%;
        cursor: row-resize;
        scroll-snap-align: var(--snap-point-align, start);
        transition: border-radius 0.2s ease-out;

        &::after {
            position: static;
            display: block;
            content: "";
            scroll-snap-align: var(--snap-point-align, end);
        }
    }

    .handle {
        position: sticky;
        top: 0;
        z-index: 12;
        display: flex;
        justify-content: center;
        cursor: pointer;

        &::before {
            content: "";
            position: absolute;
            top: 8px;
            -webkit-box-flex: 0;
            -ms-flex: 0 0 auto;
            flex: 0 0 auto;
            width: 36px;
            height: 5px;
            background-color: #ccc;
            border-radius: 5px;
        }
    }

    .sheet-cover-wrapper {
        position: sticky;
        top: 0;
        z-index: 0;
        margin-bottom: calc(-1 * var(--cover-height));
        height: var(--cover-height);
        border-radius: var(--sheet-border-radius) var(--sheet-border-radius) 0 0;
        overflow: hidden;
    }

    .sheet-cover-image {
        opacity: var(--cover-opacity, 0);
        transition: opacity 0.1s ease-out;
    }

    &.snap-position-1,
    &.snap-position-2,
    &.snap-position-3,
    &.snap-position-4,
    &.snap-position-5 {
        &:not(.nested-scroll) .sheet-wrapper.has-cover {
            position: sticky;
            top: var(--cover-height);
            z-index: 1;
        }
    }

    &.snap-position-0 {
        &:not(.nested-scroll) .sheet-wrapper.has-cover {
            position: relative;
            top: unset;
            z-index: 1;
            padding-top: var(--cover-height);
            margin-bottom: calc(-1 * var(--cover-height));
        }
    }
    .sticky-header {
        border-top: 1px solid var(--sheet-border-color);
        border-radius: var(--sheet-border-radius) var(--sheet-border-radius) 0 0;
    }
}

.e-drawer:not(.sidebar-drawer):not(.nested-scroll) {
    .sheet-container {
        min-height: 100%;
    }

    .sheet-container.has-cover::after {
        content: "";
        display: block;
        height: var(--cover-height);
    }

    .sheet-wrapper {
        height: auto;
    }

    .sheet {
        min-height: var(--sheet-min-height);
        height: 100%;
    }
}

.e-drawer:not(.sidebar-drawer).nested-scroll {
    .sheet-wrapper {
        display: flex;
        position: sticky;
        bottom: 0;
        flex-direction: column;
        justify-content: end;
        contain: strict;
        height: 100%;
    }

    .sheet {
        display: flex;
        position: sticky;
        bottom: 0;
        flex-direction: column;
        height: 100%;
        max-height: 100%;
    }

    .sheet-content {
        will-change: scroll-position;
        overflow-y: auto;
        scrollbar-gutter: stable;
    }

    .sheet-header,
    .sheet-footer {
        flex: 0 0 auto;
    }
}

.e-drawer:not(.sidebar-drawer).nested-scroll.expand-to-scroll {
    .sheet-wrapper {
        position: static;
    }

    .sheet {
        position: static;
        height: 100%;
        animation: none;
    }
}

.e-drawer:not(.sidebar-drawer).nested-scroll:not(.expand-to-scroll).is-scrolling {
    .sheet-header {
        z-index: 1;
    }

    .sheet-content {
        scrollbar-color: transparent transparent;
    }
}

// ----- Snap Points -----
.e-drawer:not(.sidebar-drawer) {
    .snap {
        position: relative;
        top: var(--snap);

        &::before {
            position: absolute;
            top: 0;
            right: 0;
            left: 0;
            height: 1px;
            content: "";
            scroll-snap-align: var(--snap-point-align, start);
        }

        &.initial {
            --snap-point-align: start;
        }

        &.snap-bottom {
            position: static;
            top: initial;
            height: auto;

            &::after {
                position: static;
                display: block;
                height: var(--sheet-max-height);
                max-height: var(--sheet-safe-max-height);
                content: "";
            }
        }
    }

    &:not(.swipe-to-dismiss) .snap.snap-bottom::before {
        scroll-snap-align: none;
    }
}

.e-drawer:not(.sidebar-drawer) {
    .sheet {
        scroll-snap-stop: always !important;
    }
}

.e-drawer:not(.sidebar-drawer).snap-position-0,
.e-drawer:not(.sidebar-drawer).snap-position-1 {

    .snap::before,
    .sheet,
    .sheet::after {
        scroll-snap-align: unset !important;
    }
}

// ----- Animations -----
@keyframes initial-snap {
    0% {
        --snap-point-align: none;
    }

    50% {
        scroll-snap-type: initial;
        --snap-point-align: none;
    }
}

// ----- Fixed Snap -----
.e-drawer:not(.sidebar-drawer).fixed-snap {
    height: min(var(--fixed-snap-height), var(--sheet-safe-max-height));
    max-height: min(var(--fixed-snap-height), var(--sheet-safe-max-height));
    overflow: visible;
    contain: unset;
    scroll-snap-type: none;
    animation: none;
    pointer-events: auto;

    .handle {
        display: none;
    }

    .sheet-container,
    .sheet-wrapper,
    .sheet {
        min-height: var(--sheet-min-height);
        height: 100%;
    }

    .sheet {
        cursor: default;
    }

    .sheet-content {
        overflow-y: auto;
    }

    .sheet-above {
        top: 0;
        bottom: auto;
        transform: translateY(-100%);
    }
}

// ----- Browser Overrides -----
@supports (-webkit-touch-callout: none) or (-webkit-hyphens: none) {
    .e-drawer:not(.sidebar-drawer) {
        display: inherit;
        animation: initial-snap 0.1s both;
    }
}

@supports (-webkit-touch-callout: none) {
    .e-drawer:not(.sidebar-drawer) {

        .sheet-content,
        .sheet-header,
        .sheet-footer {
            overflow-x: scroll;
            overscroll-behavior-x: none;
            scrollbar-width: none;

            &::after {
                display: block;
                box-sizing: content-box;
                width: calc(100% + 1px);
                height: 1px;
                padding: inherit;
                padding-left: 0;
                content: "";
            }
        }

        .sheet-content {
            scrollbar-width: auto;
        }
    }
}

// ----- Scroll-Driven -----
@supports ((animation-timeline: scroll()) and (animation-range: 0% 100%)) {
    .e-drawer:not(.sidebar-drawer) {
        scroll-timeline: --sheet-timeline y;
    }

    .e-drawer:not(.sidebar-drawer).nested-scroll .sheet {
        animation: expand-sheet-height linear forwards;
        animation-timeline: --sheet-timeline;
    }

    @keyframes expand-sheet-height {
        from {
            height: 0;
        }

        to {
            height: 100%;
        }
    }

    .e-drawer:not(.sidebar-drawer).nested-scroll.expand-to-scroll .sheet-content {
        animation: overflow-y-toggle linear forwards;
        animation-timeline: --sheet-timeline;
    }

    @keyframes overflow-y-toggle {

        0%,
        99.99% {
            overflow-y: hidden;
        }

        100% {
            overflow-y: auto;
        }
    }
}

@supports (not ((animation-timeline: scroll()) and (animation-range: 0% 100%))) {
    .e-drawer:not(.sidebar-drawer).nested-scroll .sheet {
        height: var(--sheet-position);
    }

    .e-drawer:not(.sidebar-drawer).nested-scroll.expand-to-scroll .sheet-content {
        overflow-y: hidden;
    }

    .e-drawer:not(.sidebar-drawer).nested-scroll.expand-to-scroll.snap-position-0 .sheet-content {
        overflow-y: auto;
    }

    .e-drawer:not(.sidebar-drawer).nested-scroll:not(.expand-to-scroll).is-scrolling {
        .sheet {
            transform: translateY(calc(100% - var(--sheet-position, 0)));
            height: 100%;
        }

        .sheet-content {
            transform: translateY(var(--sheet-content-offset, 0));
        }

        .sheet-footer {
            transform: translateY(calc(-1 * var(--sheet-safe-max-height) + var(--sheet-position, 0)));
        }
    }
}
</style>



